import React, { useEffect } from 'react';

const useRevealOnScroll = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    document.querySelectorAll('.landing-section').forEach((el) => {
      el.classList.add('reveal');
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
};

const RoadmapItem = ({ quarter, summary, details, tags }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="landing-timeline-item" onClick={() => setOpen(o => !o)}>
      <div className="landing-dot" />
      <div className={"glass landing-timeline-card" + (open ? ' open' : '')}>
        <h3>{quarter}</h3>
        <p className="landing-summary">{summary}</p>
        <div className="landing-tags">{tags.map((t, i) => <span key={i}>{t}</span>)}</div>
        <div className="landing-details">
          <p>{details}</p>
        </div>
      </div>
    </div>
  );
};

export default function LandingPage() {
  useRevealOnScroll();

  const items = [
    {
      quarter: 'Q3 2024 — Public Beta',
      summary: 'Core trading, watchlist, price monitor, basic alerts.',
      details: 'Expanded token coverage, UX polish, early security audit, and stability improvements.',
      tags: ['Beta', 'Stability']
    },
    {
      quarter: 'Q4 2024 — AI Signals',
      summary: 'ML-based opportunity ranking, adjustable confidence thresholds.',
      details: 'Feature gating, personalized signal feed, and research notebooks for transparency.',
      tags: ['AI', 'Signals']
    },
    {
      quarter: 'Q1 2025 — Investment Pools',
      summary: 'Managed pools with transparent on-chain reporting.',
      details: 'Performance dashboards, withdrawal windows, and multi-sig upgrades.',
      tags: ['Pools', 'Transparency']
    },
    {
      quarter: 'Q2 2025 — Mobile',
      summary: 'Optimized mobile trading with instant push notifications.',
      details: 'Biometric auth, persistent alerts, and offline-ready UI.',
      tags: ['Mobile', 'Push']
    }
  ];

  return (
    <div className="landing-root">
      <header className="landing-header">
        <div className="landing-container landing-header-inner">
          <div className="landing-brand">
            <div className="landing-logo">SP</div>
            <span className="landing-brand-name">SafePump</span>
          </div>
          <nav className="landing-nav">
            <a href="#features">Features</a>
            <a href="#analytics">Analytics</a>
            <a href="#roadmap">Roadmap</a>
            <a href="#faqs">FAQs</a>
          </nav>
          <div className="landing-actions">
            <a className="landing-btn landing-btn-ghost" href="#features">Explore</a>
            <a className="landing-btn landing-btn-primary" href="/app">Launch App</a>
          </div>
        </div>
      </header>

      <section className="landing-section landing-hero" id="hero">
        <div className="landing-container landing-hero-inner">
          <div className="landing-hero-copy">
            <h1 className="landing-hero-title">Trade Solana Smarter</h1>
            <p className="landing-hero-subtitle">A stunning, responsive landing that mirrors your trading platform’s theme with polished hover effects and lively animations.</p>
            <div className="landing-hero-ctas">
              <a className="landing-btn landing-btn-primary" href="/app">Launch App</a>
              <a className="landing-btn landing-btn-ghost" href="#features">View Features</a>
            </div>
            <div className="landing-hero-trust">
              <span>Non-custodial</span>
              <span>Realtime data</span>
              <span>Secure by design</span>
            </div>
          </div>
          <div className="landing-hero-art">
            <div className="landing-orb landing-orb-lg" />
            <div className="landing-orb landing-orb-md" />
            <div className="landing-orb landing-orb-sm" />
          </div>
        </div>
      </section>

      <section className="landing-section" id="features">
        <div className="landing-container">
          <h2 className="landing-title">Everything you need to move fast</h2>
          <p className="landing-subtitle">Four pillars designed to give you an edge.</p>
          <div className="landing-features-grid">
            <div className="glass landing-feature">
              <div className="landing-icon">🔍</div>
              <h3>Discovery</h3>
              <p>Surface trending tokens early with on-chain momentum.</p>
            </div>
            <div className="glass landing-feature">
              <div className="landing-icon">📈</div>
              <h3>Analytics</h3>
              <p>Realtime prices, liquidity, volume, and whale flow.</p>
            </div>
            <div className="glass landing-feature">
              <div className="landing-icon">🔔</div>
              <h3>Alerts</h3>
              <p>AI-curated signals at the right time — no more noise.</p>
            </div>
            <div className="glass landing-feature">
              <div className="landing-icon">⚡️</div>
              <h3>Execution</h3>
              <p>Fast, safe flows with protective checks.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section" id="analytics">
        <div className="landing-container landing-analytics-inner">
          <div className="landing-analytics-copy">
            <h2 className="landing-title">Transparent performance. Actionable insight.</h2>
            <p className="landing-subtitle">High-contrast charts and curated dashboards built for speed.</p>
            <ul className="landing-bullets">
              <li>Realtime price monitor</li>
              <li>Whale tracking and liquidity shifts</li>
              <li>Portfolio and PnL breakdowns</li>
            </ul>
            <a className="landing-btn landing-btn-primary" href="/app">Open Dashboard</a>
          </div>
          <div className="landing-analytics-art">
            <div className="glass landing-screen">
              <div className="landing-screen-header"><span></span><span></span><span></span></div>
              <div className="landing-chart">
                <div className="landing-line"></div>
                <div className="landing-grid-v"></div>
                <div className="landing-grid-h"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section" id="roadmap">
        <div className="landing-container">
          <h2 className="landing-title">Product roadmap</h2>
          <p className="landing-subtitle">Hover to preview, click to expand details.</p>
          <div className="landing-timeline">
            {items.map((it, i) => (
              <RoadmapItem key={i} {...it} />
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" id="faqs">
        <div className="landing-container">
          <h2 className="landing-title">Frequently asked</h2>
          <div className="landing-faq-grid">
            <details className="glass"><summary>Non-custodial?</summary><p>Yes. Your keys, your crypto.</p></details>
            <details className="glass"><summary>Chains?</summary><p>Solana first; cross-chain support on the roadmap.</p></details>
            <details className="glass"><summary>Alerts?</summary><p>Custom and AI-curated alerts supported.</p></details>
            <details className="glass"><summary>Pro plan?</summary><p>Free tier plus optional pro features.</p></details>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <div className="landing-brand"><div className="landing-logo">SP</div><span className="landing-brand-name">SafePump</span></div>
          <div className="landing-footer-links"><a href="#">Twitter</a><a href="#">Discord</a><a href="#">Docs</a><a href="#">Privacy</a></div>
          <div className="landing-copy">© {new Date().getFullYear()} SafePump. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}


